import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nanoid } from 'nanoid';
import { preloadVideos } from '@components/AssetPreloader';
import useAsyncEffect from 'use-async-effect';

import { useMytaverse } from '@providers/MytaverseProvider';

import EventsService from '@services/EventsService';
import BrandingsService from '@services/BrandingsService';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { getDeviceProfile, mapFollowers } from './helpers';
import { useSpotLanguageSync } from './hooks/useSpotLanguageSync';
import { IAvatarSkin } from '@interfaces/avatarSkin';
import {
  EventStatus,
  IEvent,
  IPixelStreamingClient,
} from '@interfaces/event';
import {
  IParticipant,
  IParticipantRegion,
  ParticipantState,
} from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import ParticipantsService from '@services/ParticipantsService';
import { Outlet, useParams } from 'react-router-dom';
import { IPointOfInterest } from '@interfaces/pointsOfInterest';
import { NOTIFICATION_TYPES } from '@components/Notification';

import {
  useAvatarMessage,
  useEventParticipantData,
  useInitMessage,
  useParticipantsState,
} from './hooks';
import {
  IWebsocketMessageFlag,
  IUnrealScreenSize,
  SendMutedStateMessageEnum,
  StreamingProviders,
  UpdateParticipantType,
} from './interfaces';
import { useMytaStreamsSession } from '../../components/DashboardContent/MytaStreams/MytaStreamsProvider';
import AnalyticsService from '@services/AnalyticsService';
import { SendToBriefcasePropsTypeFull } from '../../components/DashboardContent/interfaces';
import { useChatState } from '@core/chat/context';
import { useInitGameCastStream } from './hooks/streamProviders';
import { usePixelStreamingProvider } from './hooks/pixelStreamingProvider';
import { IFollowerData } from '@interfaces/followers';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { MytaverseLogger } from '@helpers/logger';
import { reportStreamInitializationEvent } from '@helpers/sentry';
import { useCoreweave } from '../../components/DashboardContent/CoreWeave/CoreWeaveProvider';
import { PixelStreaming } from '@tensorworks/libspsfrontend';
import { PixelStreaming as MytaPixelStreamingClient } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import { useGamecast } from '../../components/DashboardContent/GameCast/GameCastProvider';
import { useBackButton } from './hooks/eventListeners';
import { IGamecastVideoStats } from '../../components/DashboardContent/GameCast/interfaces';
import EventFullLoadingPage from '@core/event/pages/EventFullLoadingPage';
import { env } from 'env';

import { AnalyticsType } from '@interfaces/analytics';
import { useEventContext } from '@providers/EventProvider';
import {
  IStartStreamSessionOutput,
  StreamSessionStatus,
} from '@interfaces/gamecast';
import { default as mytaverseEventLogger } from './logger';
import {
  getClientSpecificCanShareScreenOnScreen,
  getClientSpecificCanShareWebCameraOnScreen,
  getClientSpecificCanShowWebCameraOnAvatar,
  getClientSpecificCanSpeak,
  getClientSpecificUserEventPermissions,
  getClientSpecificUserEventRole,
} from '@helpers/clientSpecificUtils';
import { isDesktop } from 'react-device-detect';
import ParticipantScopes from '@constants/scopes';
import { NavigationModeType } from '@constants/navigationMode';
import { formatBuildVersionLabel, isSinglePlayerEvent } from '@helpers/events';

export const MytaverseEventProvider = () => {
  const { eventId: currentEventId } = useParams();

  const {
    token,
    userId,
    user,
    userPermissions,
    sendJSONMessageToWebSocket,
    sendMessageToEvent,
    websocketSessionId,
    isLogoutClicked,
    sendMessageToUnreal,
    navigateToEventsPage,
    setUserPermissions,
    selectedLanguage,
  } = useMytaverse();

  const [reaction, setReaction] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openAdminSettingsModal, setOpenAdminSettingsModal] = useState(false);
  // Drone camera is an admin-tools toggle. It lives here (not in the AdminTools
  // component) so it survives that component unmounting when hub-admin-tools
  // access is lost, and so the permission-loss effect below can disable it (MV-1303).
  const [isDroneMode, setIsDroneMode] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentEvent, setCurrentEvent] = useState<IEvent>();
  const [eventLoaded, setEventLoaded] = useState<boolean>(false);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<IRoom | null>(null);
  const [developersTerminalMessages, setDevelopersTerminalMessages] = useState<
    string[]
  >([]);
  const [openLeaveRegionDialog, setOpenLeaveRegionDialog] = useState(false);
  const [gameReadyToPlay, setGameReadyToPlay] = useState(true);
  const [openCameraPublisherDialog, setOpenCameraPublisherDialog] =
    useState(false);
  const [ue5WebsocketConnected, setUe5WebsocketConnected] = useState(false);
  const [ue5CoreWeaveDisabled, setUe5CoreWeaveDisabled] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<IParticipantRegion | null>(
    null,
  );
  const [
    currentRoomDolbySpatialAudioScale,
    setCurrentRoomDolbySpatialAudioScale,
  ] = useState<number>(80);
  const [isTeleporting, setIsTeleporting] = useState(false);
  const [teleportFinished, setTeleportFinished] = useState(false);
  const [poiPreviewSrc, setPoiPreviewSrc] =
    useState<SendToBriefcasePropsTypeFull | null>(null);
  const [initMessageSended, setInitMessageSended] = useState(false);
  const [previousSkin, setPreviousSkin] = useState<IAvatarSkin | null>(null);
  const [currentEventParticipant, setCurrentEventParticipant] =
    useState<IEventParticipant | null>(null);
  const [gameCastStreamRequestSended, setGameCastStreamRequestSended] =
    useState(false);
  const [previousCustomAvatar, setPreviousCustomAvatar] = useState<
    string | null
  >(null);
  const [pointsOfInterest, setPointsOfInterest] = useState<
    IPointOfInterest[] | null
  >(null);
  const [userFiles, setUserFiles] = useState<IPointOfInterest[]>([]);
  const [isGamecastCrash, setIsGamecastCrash] = useState(false);
  const [isGamecastTimeout, setIsGamecastTimeout] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState<
    IParticipant | undefined
  >();
  const [teleportingToRoom, setTeleportingToRoom] = useState<IRoom | null>(
    null,
  );
  const [teleportEnabled, setTeleportEnabled] = useState(false);
  const [isTeleportingToRoomByUnreal, setIsTeleportingToRoomByUnreal] =
    useState(false);
  const [participants, setParticipants] = useState<IParticipant[]>([]);
  const [loadingNewParticipants, setLoadingNewParticipants] = useState(false);
  const [showAvaturnModal, setShowAvaturnModal] = useState(false);
  // const [recreateAvaturnAvatar, setRecreateAvaturnAvatar] = useState(null);
  const [openTeleportToRoomDialog, setOpenTeleportToRoomDialog] =
    useState(false);
  const [selectedTeleportRoom, setSelectedTeleportRoom] =
    useState<IRoom | null>(null);
  const [dolbyToken, setDolbyToken] = useState<string>('');
  const [streamChatToken, setStreamChatToken] = useState<string>('');
  const [streamChatMemberId, setStreamChatMemberId] = useState<string>('');
  const [enableSoulmachinesAssistant, setEnableSoulmachinesAssistant] =
    useState(false);
  const [assistantScriptUpdateTick, setAssistantScriptUpdateTick] = useState(0);

  const [showCustomSpotsEditor, setShowCustomSpotsEditor] =
    useState<boolean>(false);
  const [showCustomizeSpotModal, setShowCustomizeSpotModal] =
    useState<boolean>(false);
  const [customisationItemSelectedTick, setCustomisationItemSelectedTick] =
    useState(0);
  const [selectedCustomizationTag, setSelectedCustomizationTag] = useState<
    string | null
  >(null);
  const [roomSpots, setRoomSpots] = useState<any[]>([]);
  const [selectedCustomizationUeId, setSelectedCustomizationUeId] = useState<
    string | null
  >(null);
  const [selectedCustomizationTargetSize, setSelectedCustomizationTargetSize] =
    useState<IUnrealScreenSize | null>(null);
  const [selectedCustomizationSpotType, setSelectedCustomizationSpotType] =
    useState<'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR' | null>(null);
  const [customizationSelectionRevision, setCustomizationSelectionRevision] =
    useState(0);
  const [isBrandingModeEnabled, setIsBrandingModeEnabled] =
    useState<boolean>(false);
  const [brandingActionRequest, setBrandingActionRequest] = useState<
    'library' | 'create' | null
  >(null);
  const [brandingEditorFocus, setBrandingEditorFocus] = useState<
    'environment' | 'spots' | 'hotspots' | 'assistant'
  >('environment');
  const [isEditorInspectorOpen, setIsEditorInspectorOpen] =
    useState<boolean>(false);
  const [assistantOpenRequestNonce, setAssistantOpenRequestNonce] =
    useState<number>(0);
  const requestAssistantOpen = useCallback(() => {
    setAssistantOpenRequestNonce((prev) => prev + 1);
  }, []);
  const [pendingAssistantRegionTrigger, setPendingAssistantRegionTrigger] =
    useState<{ trigger: string; nonce: number } | null>(null);
  const requestAssistantRegionChange = useCallback((trigger: string) => {
    setPendingAssistantRegionTrigger((prev) => {
      const nonce = (prev?.nonce ?? 0) + 1;
      return { trigger, nonce };
    });
    mytaverseEventLogger.log(
      `[assistant] requestAssistantRegionChange requested`,
      { trigger },
    );
  }, []);
  const [currentBrandingId, setCurrentBrandingId] = useState<string | null>(
    null,
  );
  const [brandingSpots, setBrandingSpots] = useState<any[]>([]);
  const [brandingHotspots, setBrandingHotspots] = useState<any[]>([]);
  const [pendingEditorHotspotSelection, setPendingEditorHotspotSelection] =
    useState<{ hotspotId: string; nonce: number } | null>(null);
  const requestEditorHotspotSelection = useCallback((hotspotId: string) => {
    setPendingEditorHotspotSelection((prev) => {
      const nonce = (prev?.nonce ?? 0) + 1;
      return { hotspotId, nonce };
    });
  }, []);
  const [pendingHotspotPlacement, setPendingHotspotPlacement] = useState<{
    brandingHotspotId: string;
    isCreated: boolean;
  } | null>(null);

  useSpotLanguageSync({
    language: selectedLanguage,
    isEventActive: !!currentEvent,
    brandingSpots,
    roomSpots,
    setBrandingSpots,
    setRoomSpots,
    sendMessageToUnreal,
  });

  const [showExternalKeyboardOverlay, setShowExternalKeyboardOverlay] =
    React.useState(false);
  const [externalKeyboardConnected, setExternalKeyboardConnected] =
    React.useState(!isDesktop);

  const [isUserEnteringEvent, setIsUserEnteringEvent] = useState(false);
  const fallbackScreenshotDownloadUrlRef = useRef(null);
  const [showExitButton, setShowExitButton] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUI, setIsHideWebUI] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUIExceptAssistant, setIsHideWebUIExceptAssistant] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [isHideAllExceptTopBar, setIsHideAllExceptTopBar] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [screenshotDownloadUrl, setScreenshotDownloadUrl] = useState<
    string | null
  >(null);

  const [familiarizeState, setFamiliariseState] = useState(null);
  const [aiTriggerName, setAiTriggerName] = useState('');
  const [objectPoiId, setObjectPoiId] = useState<string | null>('');
  const [spotFullscreenViewId, setSpotFullscreenViewId] = useState<
    string | null
  >(null);
  const [spotFullscreenViewTrigger, setSpotFullscreenViewTrigger] = useState<
    string | null
  >(null);

  const {
    REACT_APP_EXTERNAL_EVENT_TAG,
    REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS = 300,
  } = env;

  useEffect(() => {
    if (!REACT_APP_EXTERNAL_EVENT_TAG) {
      return;
    }
    const hasExternalEventTag =
      currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG) === true;

    if (hasExternalEventTag) {
      setIsHideWebUI((prev) => {
        if (prev.flag) {
          return prev;
        }
        return { flag: true, timestamp: Number.MAX_SAFE_INTEGER };
      });
    }
  }, [currentEvent?.eventTags, REACT_APP_EXTERNAL_EVENT_TAG, setIsHideWebUI]);

  const [showControls, setShowControls] = useState<boolean>(true);
  const [sharingWindowFullsceen, setSharingWindowFullscreen] = useState(false);
  const [isMinimizedScreenSharing, setIsMinimizedScreenSharing] =
    useState(false);
  const [openDevelopersTerminal, setOpenDevelopersTerminal] = useState(false);
  const [pendingFollowersData, setPendingFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [acceptedFollowersData, setAcceptedFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [preserveAcceptedFollowersId, setPreserveAcceptedFollowersId] =
    useState<string[]>([]);
  const [isParticipantRoomChanged, setIsParticipantRoomChanged] =
    useState(false);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [playVideoUrl, setPlayVideoUrl] = useState<string | null>(null);
  const [coreweavePixelStreaming, setCoreweavePixelStreaming] =
    useState<PixelStreaming | null>(null);
  const [mytaPixelStreaming, setMytaPixelStreaming] =
    useState<MytaPixelStreamingClient | null>(null);
  const gamecastStats = useRef<IGamecastVideoStats | null>(null);
  const [showGamecastStats, setShowGamecastStats] = useState(false);
  const [isUEModalActionInProgress, setIsUEModalActionInProgress] =
    useState(false);

  const ueInitializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const trackEventAnalytics = React.useCallback(
    (type: AnalyticsType, payload: any = {}) => {
      if (websocketSessionId) {
        AnalyticsService.track(type, new Date().getTime(), {
          eventId: currentEventId,
          participantId: userId,
          sessionId: websocketSessionId,
          // deviceType feeds the backend device/geo stats counters (MV-1225)
          payload: { deviceType: getDeviceProfile(), ...payload },
        });
      }
    },
    [websocketSessionId],
  );

  const {
    streamingProvider,
    setStreamingProvider,
    pixelStreamingClient,
    isNativeProviderCheckPending,
  } = usePixelStreamingProvider({ currentEventId, trackEventAnalytics });
  const gameliftStreamsSessionKeyRef = useRef<string>(nanoid(20));

  // Read via ref inside generateNewGameliftStreamsSessionKey (empty useCallback deps)
  // to avoid logging a stale pixelStreamingClient.
  const pixelStreamingClientRef = useRef<IPixelStreamingClient | null>(null);
  useEffect(() => {
    pixelStreamingClientRef.current = pixelStreamingClient;
  }, [pixelStreamingClient]);

  const { setParticipantState } = useParticipantsState({
    currentParticipantId: userId as string,
    currentEventId,

    currentParticipant,
    setParticipants,

    participants,
    setCurrentParticipant,

    currentRoom,
    setCurrentRoom,
    currentRegion,
    setCurrentRegion,
    setIsParticipantRoomChanged,
    rooms,
    websocketSessionId,
    trackEventAnalytics,
  });

  const {
    participantsSound,
    gameSound,
    assistantSound,
    setGameSoundHandle,
    setParticipantsSoundHandle,
    setAssistantSoundHandle,
  } = useEventParticipantData({
    currentEvent,
    currentEventParticipant,
  });

  const { launchArgs } = useEventContext();
  //const { showNotification, getBadRequestNotification } = useNotification();
  const { setOpen: setOpenChat } = useChatState();
  const { isDebugMode } = useMytaverse();

  const generateNewGameliftStreamsSessionKey = useCallback(() => {
    if (isDebugMode) {
      return '';
    }

    const gameliftStreamsSessionKey = nanoid(20);

    mytaverseEventLogger.log(
      `New gamelift streams session key: ${gameliftStreamsSessionKey}`,
    );

    const currentPixelStreamingClient = pixelStreamingClientRef.current;
    mytaverseEventLogger.log(
      `Pixel streaming client build (live): ${formatBuildVersionLabel(currentPixelStreamingClient?.version, currentPixelStreamingClient?.clientChangeset)}`,
    );

    gameliftStreamsSessionKeyRef.current = gameliftStreamsSessionKey;

    return gameliftStreamsSessionKey;
  }, [currentEventId]);

  const { initMessageHandler } = useInitMessage({
    currentEvent,
    currentParticipant,
    currentEventParticipant,
    dolbyToken,
    teleportingToRoom,
    teleportEnabled,
    token,
    websocketSessionId,
    user,
    userPermissions,
    generateNewGameliftStreamsSessionKey,
  });

  const mytaStreams = useMytaStreamsSession({
    enabled: streamingProvider === StreamingProviders.MytaStreams,
    currentEvent,
    pixelStreamingClient,
    initMessageHandler,
    initMessageSended,
    setInitMessageSended,
    setGameReadyToPlay,
    gameSound,
    setMytaPixelStreaming,
  });

  const { avatarMessage } = useAvatarMessage({
    currentSkin: { avatarId: user?.activeAvatarId || '' } as IAvatarSkin,
    currentEvent,
    customAvatarUrl: user?.customAvatarUrl || '',
  });

  const {
    connectedPod,
    coreweaveLoading,
    dataChannelOpened,
    handleInitializeButton,
    isPlayButtonPressed,
    playStream,
    rootElement,
    setRootElement,
    setWebSocketAddress,
    togglePlayStream,
    webSocketAddress,
  } = useCoreweave({
    currentEvent,
    currentParticipant,
    currentRoom,
    gameSound,
    initMessageHandler,
    initMessageSended,
    muted,
    pixelStreamingClient,
    coreweavePixelStreaming,
    setGameReadyToPlay,
    setInitMessageSended,
    setCoreweavePixelStreaming,
    ue5CoreWeaveDisabled,
    streamingProvider,
    avatarMessage,
  });

  const {
    streamId: gameCastStreamId,
    setStreamId: setGameCastStreamId,
    streamRegion: gameCastStreamRegion,
    setStreamRegion: setGameCastStreamRegion,
  } = useInitGameCastStream(
    currentEvent,
    streamingProvider,
    pixelStreamingClient,
  );

  const {
    isStreamLoaded,
    isStreamLoading,
    setIsStreamLoaded,
    setIsStreamLoading,
    videoRef,
    audioRef,
    abortGamecastConnection,
    processActiveStreamSession,
    gamecastClientRef,
    sendMytaverseActions,
    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
    setReconnectGamecast,
    resetReconnectGamecastAttempts,
  } = useGamecast({
    currentEventId,
    currentRoom,
    trackAnalytics: trackEventAnalytics,
    gameSound,
    currentParticipant,
    setGameCastStreamId,
    initMessageHandler,
    gameCastStreamRequestSent: gameCastStreamRequestSended,
    setGameCastStreamRequestSent: setGameCastStreamRequestSended,
    setInitMessageSent: setInitMessageSended,
    gameCastStreamId,
    gameCastStreamRegion,
    setGameCastStreamRegion,
    setStreamingProvider,
    setIsGamecastCrash,
    setCurrentRoom,
    setCurrentParticipant,
    isGamecastCrash,
    currentEventParticipant,
    websocketSessionId,
    currentEvent,
    gamecastStats,
    generateNewGameliftStreamsSessionKey,
    isGamecastTimeout,
    setIsGamecastTimeout,
    pixelStreamingClient,
    externalKeyboardConnected,
    setShowExternalKeyboardOverlay,
  });

  const handleUEInitializationTimeout = useCallback(() => {
    mytaverseEventLogger.log(
      `UE initialization stuck for ${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds`,
    );

    setReconnectGamecast(true);
  }, [setReconnectGamecast]);

  const clearUEInitializationTimeout = useCallback(() => {
    if (ueInitializationTimeoutRef.current) {
      clearTimeout(ueInitializationTimeoutRef.current);
      ueInitializationTimeoutRef.current = null;
    }
  }, []);

  const initUEInitializationTimeout = useCallback(() => {
    clearUEInitializationTimeout();

    mytaverseEventLogger.log(
      `Start UE initialization timer (${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds)`,
    );

    ueInitializationTimeoutRef.current = setTimeout(
      handleUEInitializationTimeout,
      Number(REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS) * 1000,
    );
  }, [
    setIsGamecastTimeout,
    clearUEInitializationTimeout,
    handleUEInitializationTimeout,
  ]);

  const handleGamecastStreamSession = useCallback(
    async ({
      status,
      gamecastStreamSession,
      sessionKey,
    }: {
      status: StreamSessionStatus;
      gamecastStreamSession: IStartStreamSessionOutput | null;
      sessionKey?: string;
    }) => {
      const expectedSessionKey = gameliftStreamsSessionKeyRef.current;
      const isCurrentAttempt =
        !!sessionKey &&
        !!expectedSessionKey &&
        expectedSessionKey === sessionKey;

      mytaverseEventLogger.log(
        `Handle WS message: sessionId: ${
          gamecastStreamSession?.Arn
            ? gamecastStreamSession?.Arn.split('/').pop()
            : ''
        }, status: ${status}, sessionKey: ${sessionKey}`,
      );

      if (isCurrentAttempt) {
        clearGameliftStreamsSessionStatusTimeout();

        // Anchor line 2 (MV-1077): mirror the breadcrumb above as a Sentry
        // *event* (the analyser ingests events, not breadcrumbs), tagged with
        // the websocket session id so both session ids land on one event and
        // the analyser can stitch the session (0.90 'stitched' tier). The
        // emitter self-skips until streamSessionId/status/sessionKey are all
        // present, so it only fires once this attempt's stream session exists.
        reportStreamInitializationEvent({
          streamSessionId: gamecastStreamSession?.Arn
            ? gamecastStreamSession.Arn.split('/').pop()
            : undefined,
          status,
          sessionKey,
          websocketSessionId,
        });
      } else {
        mytaverseEventLogger.debug(
          `Skip side-effects for unknown sessionKey: expected ${expectedSessionKey}, got ${sessionKey}`,
        );
      }

      switch (status) {
        case StreamSessionStatus.ACTIVATING: {
          if (isCurrentAttempt) {
            initGameliftStreamsSessionStatusTimeout();
          }
          break;
        }

        case StreamSessionStatus.ACTIVE: {
          if (!isCurrentAttempt) {
            break;
          }

          if (!gamecastStreamSession) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
            break;
          }

          const { isStarted, isAborted } = await processActiveStreamSession(
            gamecastStreamSession,
          );

          if (isStarted) {
            resetReconnectGamecastAttempts();
            trackEventAnalytics(AnalyticsType.FinishGameLiftStreamsInit);
            trackEventAnalytics(AnalyticsType.StartUeInitialization);
            setIsStreamLoading(false);
            setIsStreamLoaded(true);

            if (currentEventParticipant) {
              sendMytaverseActions(currentEventParticipant);
            }

            if (isSinglePlayerEvent(currentEvent)) {
              initUEInitializationTimeout();
            }

            mytaverseEventLogger.log(
              `Started gamelift streams session: ${gamecastStreamSession.Arn?.split(
                '/',
              ).pop()}`,
            );
          } else if (!isAborted) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
          }

          break;
        }

        case StreamSessionStatus.ERROR: {
          if (!isCurrentAttempt) {
            break;
          }

          mytaverseEventLogger.log('Gamecast connection retry');
          setReconnectGamecast(true);

          break;
        }

        case StreamSessionStatus.STREAM_RETRY: {
          if (!isCurrentAttempt) {
            break;
          }

          // Transient backend failure — retry via reconnect (bounded by
          // MAX_GAMECAST_RECONNECT_ATTEMPTS). NO_MACHINES kicks immediately instead.
          mytaverseEventLogger.log(
            'Transient stream-start failure — reconnecting gamecast',
          );
          setReconnectGamecast(true);

          break;
        }

        case StreamSessionStatus.NO_MACHINES: {
          if (isCurrentAttempt) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false,
            });
          }
          break;
        }

        default:
          break;
      }
    },
    [
      processActiveStreamSession,
      setIsStreamLoading,
      setIsStreamLoaded,
      currentEventParticipant,
      sendMytaverseActions,
      navigateToEventsPage,
      pixelStreamingClient,
      initGameliftStreamsSessionStatusTimeout,
      clearGameliftStreamsSessionStatusTimeout,
      setReconnectGamecast,
      initUEInitializationTimeout,
      currentEvent,
    ],
  );

  const loadSessionIds = useCallback(() => {
    const websocketSessionIdsStr = sessionStorage.getItem('websocketSessionId');

    return websocketSessionIdsStr ? JSON.parse(websocketSessionIdsStr) : [];
  }, []);

  useEffect(() => {
    if (!websocketSessionId) {
      return;
    }

    const websocketSessionIds = loadSessionIds();
    websocketSessionIds.push(websocketSessionId);

    sessionStorage.setItem(
      'websocketSessionId',
      JSON.stringify(websocketSessionIds),
    );
  }, [websocketSessionId]);

  useEffect(() => {
    mytaverseEventLogger.log(`Open event ${currentEventId} page`);
    return () => {
      mytaverseEventLogger.log(`Leave event ${currentEventId} page`);
      clearUEInitializationTimeout();
      clearGameliftStreamsSessionStatusTimeout();
      abortGamecastConnection();
      if (!isLogoutClicked()) {
        sendJSONMessageToWebSocket({
          action: 'LEAVE_EVENT',
        });
      }
    };
  }, []);

  const cleanStates = useCallback(() => {
    setRooms([]);
    setParticipants([]);
    setOpenChat(false);
    setCurrentParticipant(undefined);
    setCurrentRoom(null);
    setCurrentEvent(undefined);
    setTeleportEnabled(false);
  }, [
    setRooms,
    setParticipants,
    setCurrentParticipant,
    setCurrentRoom,
    setCurrentEvent,
    sendJSONMessageToWebSocket,
  ]);

  useBackButton({
    cleanStates,
    initMessageSended,
    ue5WebsocketConnected,
    setUe5WebsocketConnected,
    currentEventId: currentEventId ?? null,
  });

  useEffect(() => {
    if (currentRoom) {
      localStorage.setItem('currentRoomId', currentRoom.roomId);
      mytaverseEventLogger.log(`Room changed to ${currentRoom.name}`);

      mytaverseEventLogger.log('Clear UE initialization timeout');
      clearUEInitializationTimeout();
    }
  }, [currentRoom, clearUEInitializationTimeout]);

  useEffect(() => {
    if (userFiles) {
      localStorage.setItem('userFiles', JSON.stringify(userFiles) as string);
    }
  }, [userFiles, currentRoom]);

  useEffect(() => {
    if (currentRoom) {
      setUserFiles([]);
    }
  }, [currentRoom]);

  useEffect(() => {
    const effectiveBrandingId =
      currentBrandingId ?? currentRoom?.activeBrandingId ?? null;
    if (!effectiveBrandingId) {
      setBrandingSpots([]);
      return;
    }

    BrandingsService.getBrandingSpots(effectiveBrandingId, { limit: 200 })
      .then((res) => setBrandingSpots(res.spots ?? []))
      .catch(() => setBrandingSpots([]));
  }, [currentBrandingId, currentRoom, isBrandingModeEnabled]);

  useEffect(() => {
    const effectiveBrandingId =
      currentBrandingId ?? currentRoom?.activeBrandingId ?? null;
    if (!effectiveBrandingId) {
      setBrandingHotspots([]);
      return;
    }

    BrandingsService.getBrandingHotspots(effectiveBrandingId, { limit: 200 })
      .then((res) => setBrandingHotspots(res.hotspots ?? []))
      .catch(() => setBrandingHotspots([]));
  }, [currentBrandingId, currentRoom, isBrandingModeEnabled]);

  useAsyncEffect(async () => {
    if (!currentRoom) {
      return;
    }

    if (pointsOfInterest) {
      setPointsOfInterest(null);
    }

    let pointsInterest: IPointOfInterest[] = [];

    try {
      pointsInterest = await EventsService.getPointOfInterest(
        currentRoom.roomId,
      );
    } catch (e) {
      pointsInterest = [];
    }

    if (pointsInterest?.length) {
      setPointsOfInterest(pointsInterest);
    }
  }, [currentRoom]);

  useEffect(() => {
    if (!currentEventId) {
      return;
    }

    sendJSONMessageToWebSocket({
      action: muted
        ? SendMutedStateMessageEnum.MUTE
        : SendMutedStateMessageEnum.UNMUTE,
      timestamp: Date.now(),
    });
  }, [muted, currentEventId, sendJSONMessageToWebSocket]);

  const canSpeak = useMemo(() => {
    return getClientSpecificCanSpeak(currentRoom);
  }, [currentRoom]);

  const canShowCamera = useMemo(() => {
    return getClientSpecificCanShowWebCameraOnAvatar(currentRoom);
  }, [currentRoom]);

  const canShareCamera = useMemo(() => {
    return getClientSpecificCanShareWebCameraOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  const canShareScreen = useMemo(() => {
    return getClientSpecificCanShareScreenOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  useAsyncEffect(async () => {
    if (loadingNewParticipants) {
      return;
    }
    const loadingParticipants = participants.filter(
      (p) => p.state === ParticipantState.Loading,
    );
    if (loadingParticipants.length === 0 || !currentEventId) {
      return;
    }
    setLoadingNewParticipants(true);
    const eventParticipant = await EventsService.getEvent(currentEventId);
    if (eventParticipant.event?.participants?.length !== 0) {
      setParticipants((prev) => {
        return prev.map((participant) => {
          if (participant.state === ParticipantState.Loaded) {
            return participant;
          }
          const loadedParticipant = eventParticipant.event?.participants.find(
            (p) => p.participantId === participant.participantId,
          );
          if (!loadedParticipant) {
            return participant;
          }
          const { name } = loadedParticipant;
          return {
            ...participant,
            state: ParticipantState.Loaded,
            name: name,
            fullName: name,
          };
        });
      });
    }
    setLoadingNewParticipants(false);
  }, [loadingNewParticipants, participants, currentEventId]);

  useEffect(() => {
    if (currentRoom && currentRoom.dolbySpatialAudioScale) {
      setCurrentRoomDolbySpatialAudioScale(currentRoom.dolbySpatialAudioScale);
    }
  }, [currentRoom]);

  // TODO merge it with load participant settings data
  useAsyncEffect(async () => {
    try {
      if (currentEventId && userId) {
        setLoading(true);
        setEventLoaded(false);

        setPreviousCustomAvatar(null);
        setPreviousSkin(null);

        let eventParticipant: IEventParticipant;

        const participantEventsPromise =
          ParticipantsService.getCurrentParticipantEvents();
        participantEventsPromise
          .then((events) =>
            setEvents(
              events.events.map(
                (participantEvent) => participantEvent.event as IEvent,
              ),
            ),
          )
          .catch((e) =>
            mytaverseEventLogger.log(
              `Failed to load participant events list: ${
                (e as Error).message
              }`,
            ),
          );

        try {
          eventParticipant = await EventsService.getEvent(currentEventId, {
            params: { sessionId: websocketSessionId },
          });
        } catch (e: any) {
          return navigateToEventsPage({
            message: 'notifications.loadEventDataError',
            type: NOTIFICATION_TYPES.ERROR,
          });
        }

        if (eventParticipant.event?.status !== EventStatus.ACTIVE) {
          return navigateToEventsPage({
            message: 'notifications.experienceCanceled',
            type: NOTIFICATION_TYPES.WARNING,
          });
        }

        setCurrentEvent(eventParticipant.event);

        const { eventRelease } = eventParticipant.event;
        mytaverseEventLogger.log(
          `UE server build: ${formatBuildVersionLabel(eventRelease?.serverVersion, eventRelease?.serverChangeset)}`,
        );
        mytaverseEventLogger.log(
          `EPixel streaming client build (release): ${formatBuildVersionLabel(eventRelease?.clientVersion, eventRelease?.clientChangeset)}`,
        );

        if (
          eventParticipant.event &&
          eventParticipant.event.loadingScreenAsset &&
          typeof eventParticipant.event.loadingScreenAsset !== 'string' &&
          'url' in eventParticipant.event.loadingScreenAsset
        ) {
          preloadVideos([
            (eventParticipant.event.loadingScreenAsset as any).url as string,
          ]);
        }

        setTeleportEnabled(
          eventParticipant.event?.eventTags
            ? eventParticipant.event.eventTags
                .map((tag) => tag.toLowerCase())
                .every(
                  (tag) => !['officehours', 'baseofficehours'].includes(tag),
                )
            : true,
        );

        const rooms = eventParticipant.event?.rooms || [];

        setRooms(rooms);
        const defaultRoom = rooms.find((room) => room.isDefault);
        setTeleportingToRoom(defaultRoom || null);

        const participants = eventParticipant.event?.participants
          ? eventParticipant.event.participants.map((participant) => ({
              ...participant,
              fullName: participant.name,
              state: ParticipantState.Loaded,
            }))
          : [];

        setParticipants(participants);

        const currentParticipant = participants.find(
          (participant) => participant.participantId === userId,
        );

        if (currentParticipant) {
          setCurrentParticipant({ ...currentParticipant });
        }

        const { event, ...currentEventParticipant } = eventParticipant;
        setCurrentEventParticipant(currentEventParticipant);

        setDolbyToken(eventParticipant.dolbyToken as string);
        setStreamChatToken(eventParticipant.streamChatToken as string);
        setStreamChatMemberId(eventParticipant.streamChatMemberId as string);

        if (user?.roleScopes) {
          setUserPermissions((prevState: ParticipantScopes[]) => {
            if (prevState.length > 0) {
              return prevState;
            }

            const resolved = getClientSpecificUserEventPermissions({
              role: eventParticipant.role,
              roleScopes: user.roleScopes,
            });

            console.log(
              '[userPermissions] userPermissions resolved:',
              resolved,
              'role:',
              eventParticipant.role,
              'roleScopes:',
              user.roleScopes,
            );

            return resolved;
          });

          const newUserRole = getClientSpecificUserEventRole();

          if (newUserRole !== null) {
            setCurrentEventParticipant(
              (prevState: IEventParticipant | null) => {
                if (prevState) {
                  return {
                    ...prevState,
                    role: newUserRole as string,
                  };
                }

                return prevState;
              },
            );

            setCurrentParticipant((prevState) => {
              if (prevState) {
                return {
                  ...prevState,
                  role: newUserRole,
                };
              }

              return prevState;
            });
          }
        }

        const followingData = await EventsService.getFollowingData(
          currentEventId,
        );

        const { pending, accepted } = mapFollowers(followingData.followers);

        setPendingFollowersData(pending);
        setAcceptedFollowersData(accepted);

        setEventLoaded(true);
        setLoading(false);
      } else {
        setCurrentEvent(undefined);
        setTeleportEnabled(false);
        setEventLoaded(false);

        setRooms([]);
        setCurrentRoom(null);

        setParticipants([]);
        setCurrentParticipant(undefined);
      }
    } catch (error) {
      setEventLoaded(false);
      setLoading(false);
      mytaverseEventLogger.log((error as Error).message);
      navigateToEventsPage({
        title: 'notifications.badRequest',
        message: (error as Error).message,
      });
    }
  }, [userId, currentEventId, user?.roleScopes, websocketSessionId]);

  const ownerTeleportToRoomPreserveData = React.useCallback(() => {
    if (acceptedFollowersData.length === 0 || !currentParticipant) return;

    const followers = acceptedFollowersData
      .filter(
        (follower) => follower.adminId === currentParticipant.participantId,
      )
      .map((follower) => follower.userId);

    if (followers.length === 0) return;

    setPreserveAcceptedFollowersId([...new Set(followers)]);
  }, [currentParticipant, acceptedFollowersData]);

  const ownerTeleportToRoom = React.useCallback(() => {
    if (
      preserveAcceptedFollowersId.length === 0 ||
      !currentParticipant ||
      !currentEventId
    )
      return;

    sendJSONMessageToWebSocket({
      action: WebsocketAction.OwnerTeleport,
      ownerId: currentParticipant.participantId,
      followerIds: [...preserveAcceptedFollowersId],
      ownerRoomId: currentParticipant.roomId,
      gameSessionId: currentParticipant.gameSessionId || null,
    });
  }, [
    currentParticipant,
    acceptedFollowersData,
    preserveAcceptedFollowersId,
    sendMessageToEvent,
    currentEventId,
  ]);

  useEffect(() => {
    if (isParticipantRoomChanged) {
      ownerTeleportToRoom();
      setIsParticipantRoomChanged(false);
      setPreserveAcceptedFollowersId([]);
    }
  }, [
    isParticipantRoomChanged,
    ownerTeleportToRoom,
    setPreserveAcceptedFollowersId,
  ]);

  useEffect(() => {
    if (
      currentEvent &&
      currentParticipant &&
      currentParticipant.eventId !== currentEvent.eventId
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref') || 'hub';

      sendJSONMessageToWebSocket({
        action: 'JOIN_TO_EVENT',
        timestamp: Date.now(),
        eventId: currentEvent.eventId,
        ref,
      });
    }
  }, [currentEvent, currentParticipant]);

  const updateParticipant: UpdateParticipantType = useCallback(
    (participantId, newParticipantInfo) => {
      if (
        currentParticipant &&
        currentParticipant.participantId === participantId
      ) {
        setCurrentParticipant(
          (prevParticipant) =>
            ({
              ...prevParticipant,
              ...newParticipantInfo,
              isSpeaker: !!prevParticipant?.isSpeaker,
            } as IParticipant),
        );
      }

      setParticipants((prevParticipants) =>
        prevParticipants.map((prevParticipant) =>
          prevParticipant.participantId === participantId
            ? {
                ...prevParticipant,
                ...newParticipantInfo,
              }
            : prevParticipant,
        ),
      );
    },
    [currentParticipant],
  );

  // Re-resolve the current user's event permissions and role when the role
  // changes mid-session (MV-1303). Mirrors the initial-load resolution at
  // event load, but unconditionally — the first-load path only resolves once.
  const recomputeUserEventPermissions = useCallback(
    (role: string) => {
      if (!user?.roleScopes) {
        return;
      }

      const resolved = getClientSpecificUserEventPermissions({
        role,
        roleScopes: user.roleScopes,
      });

      setUserPermissions(resolved);

      const clientSpecificRole = getClientSpecificUserEventRole();
      const effectiveRole =
        clientSpecificRole !== null ? clientSpecificRole : role;

      setCurrentEventParticipant((prevState) =>
        prevState ? { ...prevState, role: effectiveRole as string } : prevState,
      );
    },
    [user, setUserPermissions],
  );

  // When the current user is in drone mode but loses hub-admin-tools access
  // (e.g. their experience role is downgraded in realtime, MV-1303), UE5 must
  // be told to leave the drone camera. Otherwise the detached drone camera
  // stays active with no avatar and the user appears stuck in first-person.
  // This covers the permission-loss path that unmounts <AdminTools/> — its
  // local toggle would drop the drone state silently, never sending disable.
  useEffect(() => {
    if (!isDroneMode) {
      return;
    }

    const hasAdminTools = !!userPermissions?.includes(
      ParticipantScopes.HubAdminTools,
    );

    if (hasAdminTools) {
      return;
    }

    sendMessageToUnreal({ action: WebsocketAction.DisableDroneCamera });
    setIsDroneMode(false);
  }, [userPermissions, isDroneMode, sendMessageToUnreal]);

  const selectNavigationMode = useCallback(
    async (mode: NavigationModeType) => {
      try {
        await ParticipantsService.updateCurrentParticipantProfile({
          navigationMode: mode,
        });
        setCurrentEventParticipant((prev) => ({
          ...prev,
          navigationMode: mode,
        }));
      } catch (error) {
        console.error(
          `Failed to update navigation mode: ${(error as Error).message}`,
          { participantId: currentParticipant?.participantId },
        );
      }
    },
    [currentParticipant?.participantId],
  );

  const resetPreviousSkin = useCallback(() => {
    if (previousSkin) {
      setPreviousSkin(null);
    }
  }, [previousSkin, setPreviousSkin]);

  const resetPreviousCustomSkin = useCallback(() => {
    if (previousCustomAvatar) {
      setPreviousCustomAvatar(null);
    }
  }, [previousCustomAvatar, setPreviousCustomAvatar]);

  const setRoomDolbySpatialAudioScale = useCallback(
    (roomId: string, dolbySpatialAudioScale: number) => {
      if (currentRoom && currentRoom.roomId === roomId) {
        setCurrentRoomDolbySpatialAudioScale(dolbySpatialAudioScale);
      }
      /*
      setCurrentRoom((prev) => {
        if (!prev || prev.id !== roomId) {
          return prev;
        }

        return {
          ...prev,
          dolbySpatialAudioScale,
        };
      });
       */

      setRooms((prev) =>
        prev.map((room) => {
          if (room.roomId === roomId) {
            return {
              ...room,
              dolbySpatialAudioScale,
            };
          }

          return room;
        }),
      );
    },
    [currentRoom],
  );

  const updateRoomScale = useCallback(
    async (value: number) => {
      if (currentRoom && currentEventId) {
        await EventsService.updateRoomSpatialScale(
          currentRoom.roomId as string,
          value,
          currentEventId,
        );

        setRoomDolbySpatialAudioScale(currentRoom.roomId, value);
      }
    },
    [currentRoom, currentEventId, setRoomDolbySpatialAudioScale],
  );

  const teleportToRoom = React.useCallback(
    (room: IRoom | null) => {
      setTeleportingToRoom(room);
      //TODO add is speaker to message
      if (room && currentParticipant && !isTeleporting) {
        MytaverseLogger.log(`Teleporting to room: ${room.roomId} ${room.name}`);
        setIsTeleporting(true);
        ownerTeleportToRoomPreserveData();
        sendJSONMessageToWebSocket({
          action: WebsocketAction.TeleportToRoom,
          roomId: room.roomId,
          isSpeaker: currentParticipant.isSpeaker,
        });
      }
    },
    [
      setTeleportingToRoom,
      sendJSONMessageToWebSocket,
      currentParticipant,
      setIsTeleporting,
      isTeleporting,
      ownerTeleportToRoomPreserveData,
    ],
  );

  const teleportToParticipant = React.useCallback(
    async (participant: IParticipant | null) => {
      if (!participant || !currentRoom) {
        return;
      }

      MytaverseLogger.log(
        `Teleporting to participant: ${participant.participantId} ${participant.fullName}`,
      );

      const { roomId } = await ParticipantsService.getParticipantRoom(
        participant.participantId,
      );

      if (currentRoom.roomId !== roomId) {
        setIsTeleporting(true);
        ownerTeleportToRoomPreserveData();
      }

      sendMessageToUnreal({
        action: WebsocketAction.TeleportToParticipant,
        participantId: participant.participantId,
        roomId: currentRoom?.roomId,
        CurrentGameSessionId: participant.gameSessionId || null,
      });
    },
    [currentRoom, sendMessageToUnreal, ownerTeleportToRoomPreserveData],
  );

  const teleportRef = useRef<NodeJS.Timeout | null>(null);
  const ue5WebsocketConnectedRef = useRef(false);

  useEffect(() => {
    ue5WebsocketConnectedRef.current = ue5WebsocketConnected;
  }, [ue5WebsocketConnected]);

  useEffect(() => {
    if (isTeleporting && currentRoom && !teleportRef.current) {
      teleportRef.current = setInterval(() => {
        if (ue5WebsocketConnectedRef.current && teleportRef.current) {
          setIsTeleporting(false);
          clearInterval(teleportRef.current);
        }
      }, 5000);

      return;
    }

    if (teleportRef.current && !isTeleporting) {
      clearInterval(teleportRef.current);
      teleportRef.current = null;
      return;
    }

    return () => {
      if (teleportRef.current) {
        clearInterval(teleportRef.current);
        teleportRef.current = null;
      }
    };
  }, [isTeleporting, currentRoom]);

  useEffect(() => {
    if (isTeleportingToRoomByUnreal) {
      ownerTeleportToRoomPreserveData();
    }
  }, [isTeleportingToRoomByUnreal, ownerTeleportToRoomPreserveData]);

  useEffect(() => {
    if (
      currentEvent?.eventTags?.includes('ExtraLaunchArgsRequired') &&
      (!launchArgs || launchArgs.length === 0)
    ) {
      mytaverseEventLogger.log(
        'Redirect to events page because no extra launch args',
      );
      navigateToEventsPage({ message: 'notifications.noExtraLaunchArgs' });
    }
  }, [currentEvent?.eventTags, launchArgs, navigateToEventsPage]);

  const resetKeyboardPressedKeys = React.useCallback(() => {
    const videoElement = document.getElementById('gamecastVideoTag');

    if (videoElement) {
      const keysPressed = [
        'W',
        'A',
        'S',
        'D',
        'ArrowUp',
        'ArrowLeft',
        'ArrowDown',
        'ArrowRight',
      ].map((key) => {
        return {
          key,
          code: key.length === 1 ? `Key${key}` : key,
          bubbles: true,
          cancelable: true,
        };
      });

      Object.values(keysPressed).forEach((event) => {
        MytaverseLogger.log(`Dispatch keyup: ${event.key}`);
        videoElement.dispatchEvent(new KeyboardEvent('keyup', event));
      });
    }
  }, []);

  const setMutedHandler = React.useCallback(
    (value: boolean | undefined) => {
      setMuted(value || !muted);
      trackEventAnalytics(
        value || !muted ? AnalyticsType.MicOff : AnalyticsType.MicOn,
      );
    },
    [muted, setMuted, trackEventAnalytics],
  );

  const toggleRaiseHand = React.useCallback(() => {
    const nextState = !isHandRaised;

    setIsHandRaised(nextState);

    sendJSONMessageToWebSocket({
      action: 'RAISE_HAND',
      active: nextState ? true : false,
    });

    trackEventAnalytics(AnalyticsType.NavbarButtonClick, {
      name: 'raise-hand',
      active: nextState,
    });
  }, [isHandRaised, sendJSONMessageToWebSocket, trackEventAnalytics]);

  const setEmojiHandler = React.useCallback(
    (emoji: string | null) => {
      setEmoji(emoji);

      sendJSONMessageToWebSocket({
        action: 'SEND_EMOJI',
        emoji,
      });
    },
    [setEmoji, sendJSONMessageToWebSocket],
  );

  const setReactionHandler = React.useCallback(
    (reaction: string | null) => {
      setReaction(reaction);

      sendJSONMessageToWebSocket({
        action: 'TOGGLE_REACTION',
        reactionId: reaction,
      });
      trackEventAnalytics(AnalyticsType.ToggleReaction, { reaction });
    },
    [setReaction, sendJSONMessageToWebSocket, trackEventAnalytics],
  );

  const setSharingWindowFullscreenHandle = useCallback(() => {
    setSharingWindowFullscreen(!sharingWindowFullsceen);
  }, [setSharingWindowFullscreen, sharingWindowFullsceen]);

  if (loading) {
    return <EventFullLoadingPage />;
  }

  return (
    <Outlet
      context={{
        currentEventId: currentEventId as string,
        currentParticipantId: userId as string,

        currentEventParticipant,
        playVideoUrl,
        setPlayVideoUrl,
        reaction,
        emoji,
        muted,
        eventLoaded,
        userFiles,
        previousSkin,
        previousCustomAvatar,
        rooms,
        currentEvent,
        setCurrentEvent,
        currentRoom,
        currentRegion,
        currentParticipant,
        gameCastStreamRequestSended,
        isTeleporting,
        setIsTeleporting,
        teleportFinished,
        setTeleportFinished,
        setGameCastStreamRequestSended,
        setCurrentParticipant,
        setCurrentEventParticipant,
        initMessageHandler,
        gameReadyToPlay,
        setGameReadyToPlay,
        participants,
        teleportingToRoom,
        teleportEnabled,
        pointsOfInterest,
        loading,
        dolbyToken,
        streamChatToken,
        streamChatMemberId,
        openLeaveRegionDialog,
        showControls,
        poiPreviewSrc,
        initMessageSended,
        sharingWindowFullsceen,
        participantsSound,
        selectedTeleportRoom,
        gameSound,
        openTeleportToRoomDialog,
        openAdminSettingsModal,
        openCameraPublisherDialog,
        setOpenCameraPublisherDialog,
        isStreamLoaded,
        isStreamLoading,
        setIsStreamLoaded,
        setIsStreamLoading,
        //streamSession,
        videoRef,
        audioRef,
        setOpenAdminSettingsModal,
        streamingProvider,
        gameCastStreamId,
        developersTerminalMessages,
        setDevelopersTerminalMessages,
        setGameCastStreamId,
        gameCastStreamRegion,
        setGameCastStreamRegion,
        setStreamingProvider,
        isNativeProviderCheckPending,
        mytaStreams,
        setGameSound: setGameSoundHandle,
        setUe5WebsocketConnected,
        ue5WebsocketConnected,
        isMinimizedScreenSharing,
        openDevelopersTerminal,
        setOpenDevelopersTerminal,
        setIsMinimizedScreenSharing,
        setOpenTeleportToRoomDialog,
        canSpeak,
        canShowCamera,
        canShareCamera,
        canShareScreen,
        shouldConnectToConference: !isSinglePlayerEvent(currentEvent),
        setSelectedTeleportRoom,
        setParticipantsSound: setParticipantsSoundHandle,
        assistantSound,
        setAssistantSound: setAssistantSoundHandle,
        setOpenLeaveRegionDialog,
        setSharingWindowFullscreen: setSharingWindowFullscreenHandle,
        setInitMessageSended,
        setPoiPreviewSrc,
        setShowControls,
        setParticipants,
        setReaction: setReactionHandler,
        setEventLoaded,
        setMuted: setMutedHandler,
        setEmoji: setEmojiHandler,
        isHandRaised,
        setIsHandRaised,
        toggleRaiseHand,
        setUserFiles,
        resetPreviousSkin,
        resetPreviousCustomSkin,
        setCurrentRoom,
        setCurrentRegion,
        currentRoomDolbySpatialAudioScale,
        cleanStates,
        connectedPod,
        coreweaveLoading,
        dataChannelOpened,
        handleInitializeButton,
        isPlayButtonPressed,
        pixelStreaming: coreweavePixelStreaming,
        mytaPixelStreaming,
        setMytaPixelStreaming,
        playStream,
        rootElement,
        setRootElement,
        setWebSocketAddress,
        togglePlayStream,
        webSocketAddress,
        setTeleportingToRoom,
        teleportToRoom,
        teleportToParticipant,
        isTeleportingToRoomByUnreal,
        setIsTeleportingToRoomByUnreal,
        setParticipantState,
        updateParticipant,
        recomputeUserEventPermissions,
        isDroneMode,
        setIsDroneMode,
        selectNavigationMode,

        setRoomDolbySpatialAudioScale,
        updateRoomScale,
        showAvaturnModal,
        setShowAvaturnModal,
        // recreateAvaturnAvatar,
        // setRecreateAvaturnAvatar,
        trackEventAnalytics,
        pixelStreamingClient,
        pendingFollowersData,
        setPendingFollowersData,
        acceptedFollowersData,
        setAcceptedFollowersData,
        enableSoulmachinesAssistant,
        setEnableSoulmachinesAssistant,
        assistantScriptUpdateTick,
        setAssistantScriptUpdateTick,
        ue5CoreWeaveDisabled,
        setUe5CoreWeaveDisabled,
        isGamecastCrash,
        setIsGamecastCrash,
        events,
        gamecastStats,
        showGamecastStats,
        setShowGamecastStats,
        showExitButton,
        setShowExitButton,
        isHideWebUI,
        setIsHideWebUI,
        isHideWebUIExceptAssistant,
        setIsHideWebUIExceptAssistant,
        isHideAllExceptTopBar,
        setIsHideAllExceptTopBar,
        screenshotDownloadUrl,
        setScreenshotDownloadUrl,
        fallbackScreenshotDownloadUrlRef,
        isUEModalActionInProgress,
        setIsUEModalActionInProgress,
        processActiveStreamSession,
        gamecastClientRef,
        sendMytaverseActions,
        handleGamecastStreamSession,
        gameliftStreamsSessionKey: gameliftStreamsSessionKeyRef.current,
        generateNewGameliftStreamsSessionKey,
        showExternalKeyboardOverlay,
        setShowExternalKeyboardOverlay,
        externalKeyboardConnected,
        setExternalKeyboardConnected,
        isUserEnteringEvent,
        setIsUserEnteringEvent,
        showCustomSpotsEditor,
        setShowCustomSpotsEditor,
        showCustomizeSpotModal,
        setShowCustomizeSpotModal,
        customisationItemSelectedTick,
        setCustomisationItemSelectedTick,
        selectedCustomizationTag,
        setSelectedCustomizationTag,
        roomSpots,
        setRoomSpots,
        selectedCustomizationUeId,
        setSelectedCustomizationUeId,
        selectedCustomizationTargetSize,
        setSelectedCustomizationTargetSize,
        selectedCustomizationSpotType,
        setSelectedCustomizationSpotType,
        customizationSelectionRevision,
        setCustomizationSelectionRevision,
        isBrandingModeEnabled,
        setIsBrandingModeEnabled,
        brandingActionRequest,
        setBrandingActionRequest,
        brandingEditorFocus,
        setBrandingEditorFocus,
        isEditorInspectorOpen,
        setIsEditorInspectorOpen,
        assistantOpenRequestNonce,
        requestAssistantOpen,
        pendingAssistantRegionTrigger,
        requestAssistantRegionChange,
        currentBrandingId,
        setCurrentBrandingId,
        brandingSpots,
        setBrandingSpots,
        brandingHotspots,
        setBrandingHotspots,
        pendingEditorHotspotSelection,
        setPendingEditorHotspotSelection,
        requestEditorHotspotSelection,
        pendingHotspotPlacement,
        setPendingHotspotPlacement,
        familiarizeState,
        setFamiliariseState,
        resetKeyboardPressedKeys,
        aiTriggerName,
        setAiTriggerName,
        objectPoiId,
        setObjectPoiId,
        spotFullscreenViewId,
        setSpotFullscreenViewId,
        spotFullscreenViewTrigger,
        setSpotFullscreenViewTrigger,
      }}
    />
  );
};
